import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/Reveal";
import { POSTS, type PostMeta } from "@/content/posts";
import { ArrowUpRight, ArrowLeft, Search, Calendar, Clock, Sparkles, X } from "lucide-react";
import { buildSeo } from "@/lib/seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { categoryToSlug } from "@/content/blog-categories";

const PAGE_SIZE = 12;
const ALL = "All";

type BlogSearch = { page?: number; category?: string; q?: string };

export const Route = createFileRoute("/blog/")({
  validateSearch: (raw: Record<string, unknown>): BlogSearch => {
    const page = Math.max(1, Number(raw.page) || 1);
    const category = typeof raw.category === "string" && raw.category.length ? raw.category : ALL;
    const q = typeof raw.q === "string" ? raw.q.slice(0, 80) : "";
    // Omit defaults so a bare /blog stays a clean, canonical URL instead of
    // redirecting crawlers to /blog?page=1&category=All&q=
    const out: BlogSearch = {};
    if (page > 1) out.page = page;
    if (category !== ALL) out.category = category;
    if (q) out.q = q;
    return out;
  },
  head: () => buildSeo({
    title: "SEO Blog — AEO, Technical & Local SEO Field Notes | Freelancer Tamal",
    description: "Plain-English articles on AEO, technical SEO, local SEO, original research, and digital marketing — written by a working consultant in Bangladesh.",
    path: "/blog",
    ogEyebrow: "Blog",
    keywords: "SEO blog Bangladesh, AEO guide, technical SEO articles, local SEO playbook, GEO strategy, SEO tutorials",
    alternates: { bn: "/bn/blog" },
  }),
  component: BlogIndex,
});

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

function BlogIndex() {
  const { page = 1, category = ALL, q = "" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [query, setQuery] = useState(q);

  // Keep the input in sync with the URL (back/forward, hydration, cleared search).
  useEffect(() => {
    setQuery(q);
  }, [q]);

  // All posts sorted newest-first.
  const sorted = useMemo<PostMeta[]>(
    () => [...POSTS].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [],
  );

  // Categories with counts.
  const categories = useMemo(() => {
    const map = new Map<string, number>();
    for (const p of sorted) {
      const c = (p.category || "").trim();
      if (!c || c.length > 40) continue; // skip garbage long values
      map.set(c, (map.get(c) ?? 0) + 1);
    }
    return [{ name: ALL, count: sorted.length }, ...Array.from(map, ([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)];
  }, [sorted]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return sorted.filter((p) => {
      if (category !== ALL && p.category !== category) return false;
      if (!needle) return true;
      return (
        p.title.toLowerCase().includes(needle) ||
        p.excerpt.toLowerCase().includes(needle) ||
        p.category.toLowerCase().includes(needle)
      );
    });
  }, [sorted, category, q]);

  // Featured = newest post, but only on the unfiltered first page.
  const isHome = category === ALL && !q && page === 1;
  const featured = isHome ? sorted[0] : null;
  const list = isHome ? filtered.slice(1) : filtered;

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageItems = list.slice(start, start + PAGE_SIZE);

  function setSearch(next: Partial<BlogSearch>) {
    navigate({
      search: (prev) => {
        const merged: BlogSearch = { ...prev, ...next };
        // Reset page when filters change.
        if (next.category !== undefined || next.q !== undefined) merged.page = 1;
        return merged;
      },
      replace: true,
    });
  }

  function submitSearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch({ q: query.trim() });
  }

  // SEO schema — Blog + ItemList of visible posts on this page.
  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Freelancer Tamal — SEO Blog",
    url: `${SITE.url}/blog`,
    blogPost: pageItems.slice(0, 12).map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      url: `${SITE.url}/blog/${p.slug}`,
      datePublished: p.date,
      author: { "@type": "Person", name: SITE.fullName },
      articleSection: p.category,
    })),
  };

  return (
    <>
      <JsonLd data={itemListSchema} />
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]} />

      {/* Editorial hero */}
      <header className="container-x pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary mb-6">
              <Sparkles size={12} className="opacity-80" />
              The Field Journal · {sorted.length} articles
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.98]">
              SEO, AEO &amp; GEO <span className="text-gradient">field notes.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Long-form, plain-English writing on what is actually working in
              search and answer engines right now — no fluff, no AI slop.
              Pulled straight from live client engagements.
            </p>
          </div>

          <form onSubmit={submitSearch} className="lg:col-span-4 w-full" role="search" aria-label="Search blog">
            <label htmlFor="blog-search" className="sr-only">Search articles</label>
            <div className="relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="blog-search"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search articles…"
                className="w-full h-12 rounded-full bg-card border border-border pl-11 pr-24 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 focus:shadow-[var(--shadow-glow)] transition"
              />
              <button
                type="submit"
                className="absolute right-1.5 top-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-wider hover:opacity-90 transition"
              >
                Search
              </button>
            </div>
            {q && (
              <button
                type="button"
                onClick={() => { setQuery(""); setSearch({ q: "" }); }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <X size={12} /> Clear "{q}"
              </button>
            )}
          </form>
        </div>

        {/* Category nav */}
        <nav aria-label="Blog categories" className="mt-12">
          <ul className="flex flex-wrap gap-2">
            {categories.map((c) => {
              const active = c.name === category;
              const isAll = c.name === ALL;
              return (
                <li key={c.name}>
                  {isAll ? (
                    <Link
                      to="/blog"
                      search={{ page: 1, category: ALL, q: "" }}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.16em] transition ${
                        active
                          ? "bg-primary text-primary-foreground border-primary shadow-[var(--shadow-glow)]"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                      }`}
                    >
                      {c.name}
                      <span className={`text-[10px] tabular-nums ${active ? "opacity-80" : "opacity-60"}`}>
                        {c.count}
                      </span>
                    </Link>
                  ) : (
                    <Link
                      to="/blog/category/$category"
                      params={{ category: categoryToSlug(c.name) }}
                      className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-xs uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
                    >
                      {c.name}
                      <span className="text-[10px] tabular-nums opacity-60">{c.count}</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>
      </header>

      <div className="container-x pb-32 space-y-20">
        {/* Featured (only on the home view) */}
        {featured && (
          <Reveal>
            <Link
              to="/blog/$slug"
              params={{ slug: featured.slug }}
              className="group relative block overflow-hidden rounded-[2rem] border border-border bg-card p-10 lg:p-16 hover:border-primary/40 hover:shadow-[var(--shadow-glow)] transition-all"
            >
              <div className="absolute inset-0 opacity-60 [background:var(--gradient-radial)] pointer-events-none" />
              <div className="relative grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-8">
                  <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.22em]">
                    <span className="rounded-full bg-primary/15 text-primary px-3 py-1">Featured</span>
                    <span className="text-muted-foreground">{featured.category}</span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5"><Calendar size={11} /> {formatDate(featured.date)}</span>
                    <span className="text-muted-foreground inline-flex items-center gap-1.5"><Clock size={11} /> {featured.readMin} min</span>
                  </div>
                  <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
                    {featured.title}
                  </h2>
                  <p className="mt-6 text-lg text-muted-foreground max-w-2xl">{featured.excerpt}</p>
                </div>
                <div className="lg:col-span-4 lg:border-l lg:border-border/60 lg:pl-10 flex lg:flex-col items-start justify-between gap-6">
                  <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Editor's pick</div>
                  <div className="inline-flex items-center gap-2 text-sm text-primary">
                    Read article
                    <ArrowUpRight size={16} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                  </div>
                </div>
              </div>
            </Link>
          </Reveal>
        )}

        {/* Result meta */}
        <div className="flex items-end justify-between gap-4 -mb-10">
          <h2 className="font-display text-2xl md:text-3xl">
            {q
              ? <>Results for <span className="text-gradient">“{q}”</span></>
              : category === ALL
                ? "Latest articles"
                : <>Filed under <span className="text-gradient">{category}</span></>}
          </h2>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {list.length === 0
              ? "No matches"
              : `Showing ${start + 1}–${Math.min(start + PAGE_SIZE, list.length)} of ${list.length}`}
          </p>
        </div>

        {(q || category !== ALL) && (
          <div className="-mb-10">
            <Link
              to="/blog"
              search={{ page: 1, category: ALL, q: "" }}
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition"
            >
              <ArrowLeft size={14} /> Back to all articles
            </Link>
          </div>
        )}

        {/* Grid */}
        {pageItems.length === 0 ? (
          <div className="rounded-3xl border border-border p-16 text-center">
            <p className="text-lg text-muted-foreground">
              No articles match your filters. Try{" "}
              <Link to="/blog" search={{ page: 1, category: ALL, q: "" }} className="text-primary underline-offset-4 hover:underline">
                clearing filters
              </Link>.
            </p>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pageItems.map((p, i) => (
              <li key={p.slug} className="cv-card">
                <Reveal delay={(i % 3) * 0.05}>
                  <Link
                    to="/blog/$slug"
                    params={{ slug: p.slug }}
                    className="group relative flex h-full flex-col rounded-3xl border border-border bg-card/40 p-8 hover:bg-card hover:border-primary/40 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em]">
                      <span className="text-primary">{p.category}</span>
                      <span className="text-muted-foreground inline-flex items-center gap-1.5">
                        <Clock size={11} /> {p.readMin} min
                      </span>
                    </div>
                    <h3 className="mt-5 font-display text-2xl leading-snug group-hover:text-gradient">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-sm text-muted-foreground line-clamp-3 flex-1">{p.excerpt}</p>
                    <div className="mt-8 flex items-center justify-between text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> {formatDate(p.date)}</span>
                      <ArrowUpRight size={14} className="group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition" />
                    </div>
                  </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav aria-label="Blog pagination" className="flex flex-wrap items-center justify-center gap-2 pt-8">
            <PageLink disabled={safePage <= 1} to={safePage - 1} label="Previous" />
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => {
              // Compact: show first, last, current ±1, and ellipsis.
              const show =
                n === 1 || n === totalPages || Math.abs(n - safePage) <= 1;
              const showEllipsis =
                (n === 2 && safePage > 3) || (n === totalPages - 1 && safePage < totalPages - 2);
              if (!show) {
                return showEllipsis ? (
                  <span key={n} className="px-2 text-muted-foreground">…</span>
                ) : null;
              }
              const active = n === safePage;
              return (
                <Link
                  key={n}
                  to="/blog"
                  search={(prev) => ({ ...prev, page: n })}
                  aria-current={active ? "page" : undefined}
                  className={`min-w-10 h-10 px-3 inline-flex items-center justify-center rounded-full border text-sm tabular-nums transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
                  }`}
                >
                  {n}
                </Link>
              );
            })}
            <PageLink disabled={safePage >= totalPages} to={safePage + 1} label="Next" />
          </nav>
        )}
      </div>
    </>
  );
}

function PageLink({ to, label, disabled }: { to: number; label: string; disabled: boolean }) {
  if (disabled) {
    return (
      <span className="h-10 px-4 inline-flex items-center rounded-full border border-border text-sm text-muted-foreground/40 cursor-not-allowed">
        {label}
      </span>
    );
  }
  return (
    <Link
      to="/blog"
      search={(prev) => ({ ...prev, page: to })}
      className="h-10 px-4 inline-flex items-center rounded-full border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition"
    >
      {label}
    </Link>
  );
}